import clone from "lodash/clone";
import React from "react";
import {FiLoader} from "react-icons/fi";

import Button from "@/components/button/Button.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {MonitoringServerSelect} from "@/components/monitoringServerSelect/MonitoringServerSelect.tsx";
import Ajax from "@/legacy/general/Ajax.js";
import {TaskStatus} from "@/legacy/runner/TaskStatus.jsx";
import Utils from "@/legacy/Utils.js";
import {URL_ORIGINAL_WEBSITE} from "@/lib/links.ts";

export default class TestRunner extends React.Component {
  constructor(props) {
    super(props);
    Utils.autoBindClass(this);

    this.state = {
      formData: {
        test_location: this.props.locations && this.props.locations.length ? this.props.locations[0].id : null,
      },
      isRunningTest: false,
      testStatus: null,
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.data === prevProps.data) {
      return;
    }
    this.resetRunTest();
    const formData = this.getFormData();
    const oldCheckType = prevProps.data.monitoring_service_type;
    const newCheckType = this.props.data.monitoring_service_type;
    const oldVersion = prevProps.data.msp_version;
    const newVersion = this.props.data.msp_version;

    // If selectedLocation prop has changed, update the local state
    if (this.props.selectedLocation !== prevProps.selectedLocation && this.props.selectedLocation) {
      formData.test_location = this.props.selectedLocation.id;
      this.setState({formData});
      return;
    }

    if (
      this.props.locations &&
      this.props.locations.length &&
      (oldCheckType !== newCheckType || oldVersion !== newVersion)
    ) {
      formData.test_location = this.props.locations[0].id;
      this.setState({formData});
    }
  }

  getFormData() {
    return this.state.formData;
  }

  resetRunTest() {
    this.setState({
      isRunningTest: false,
      testStatus: null,
    });
  }

  async handleRunTest(e) {
    e.preventDefault();
    this.resetRunTest();
    this.setState({
      isRunningTest: true,
    });
    if (this.props.onStart) {
      this.props.onStart();
    }
    const serviceData = clone(this.props.data) || {};
    if (serviceData.monitoring_groups && !Array.isArray(serviceData.monitoring_groups)) {
      serviceData.monitoring_groups = [serviceData.monitoring_groups];
    }

    // Use selectedLocation from props if available, otherwise use local state
    const locationId = this.props.selectedLocation ? this.props.selectedLocation.id : this.getFormData().test_location;

    serviceData.test_location = locationId;
    if (this.props.scriptGetter) {
      serviceData.msp_script = this.props.scriptGetter();
    }

    new Ajax().post({
      url: `${URL_ORIGINAL_WEBSITE}${this.props.runTestURL}`,
      data: serviceData,
      encoder: "json",
      decoder: "json",
      success: function (result) {
        if (!result.success) {
          this.resetRunTest();
          if (this.props.onError) {
            this.props.onError(result);
          }
        } else if (!result.data.task_id) {
          // results of a test immediately available
          this.setState({
            isRunningTest: false,
          });
          if (this.props.onSuccess) {
            this.props.onSuccess(result.data);
          }
        } else {
          this.setState(
            {
              isRunningTest: true,
            },
            () => {
              this.loadTimer = 0;
              this.pollResults(result.data.task_id);
            }
          );
        }
      }.bind(this),
      error: function () {
        this.resetRunTest();
        if (this.props.onError) {
          this.props.onError({
            result: "Probe server responded with an error.",
            status: TaskStatus.FAILED,
            serverId: locationId,
          });
        }
      }.bind(this),
    });
  }

  _isPollingTimedOut() {
    if (this.state.testStatus === TaskStatus.NEW && this.loadTimer >= 10000) {
      return "The test server queue is currently full, please try again later or try using a different test server.";
    }
    const mst = this.props.data.monitoring_service_type;
    let timeout = 61;
    if (["TRANSACTION", "API"].includes(mst)) {
      timeout = 91;
    } else if (["PAGESPEED"].includes(mst)) {
      timeout = 181;
    }
    if (this.state.testStatus === TaskStatus.RUNNING) {
      if (this.loadTimer >= timeout * 1000) {
        return "Probe server timed out.";
      }
    }
    return null;
  }

  pollResults(task_id) {
    if (!this.state.isRunningTest) {
      this.loadTimer = 0;
      return;
    }
    let timeout = this._isPollingTimedOut();
    if (timeout) {
      this.setState({
        testStatus: TaskStatus.FAILED,
        isRunningTest: false,
      });
      if (this.props.onError) {
        this.props.onError({
          result: timeout,
          status: TaskStatus.FAILED,
          serverId: locationId,
        });
      }
      return;
    }

    new Ajax().get({
      url: `${URL_ORIGINAL_WEBSITE}${this.props.runTestURL}`,
      data: {
        task_id: task_id,
      },
      decoder: "json",
      success: function (result) {
        const stillRunning = result.success
          ? result.data.status === TaskStatus.NEW || result.data.status === TaskStatus.RUNNING
          : false;
        if (result.success) {
          this.setState({
            isRunningTest: stillRunning,
            testStatus: result.data.status,
          });
        } else {
          this.setState({
            testStatus: TaskStatus.FAILED,
            isRunningTest: stillRunning,
          });
        }
        if (stillRunning) {
          this.loadTimer += 2000;
          setTimeout(this.pollResults.bind(this), 2000, task_id);
        } else {
          if (this.props.onSuccess) {
            this.props.onSuccess(result.data);
          }
        }
      }.bind(this),
      error: function (jqXHR, textStatus) {
        this.resetRunTest();
        if (this.props.onError) {
          this.props.onError({
            result: textStatus,
            status: TaskStatus.FAILED,
            serverId: locationId,
          });
        }
      }.bind(this),
    });
  }

  render() {
    // Determine the selected location
    const selectedLocation =
      this.props.selectedLocation ||
      this.props.locations.find((l) => l.id === this.state.formData.test_location) ||
      (this.props.locations.length ? this.props.locations[0] : null);

    return (
      <>
        <div className="d-flex gap-3 flex-column">
          <MonitoringServerSelect
            className="flex-grow-1"
            defaultValue={this.props.locations[0]}
            options={this.props.locations || []}
            value={selectedLocation}
            onChange={(v) => {
              this.setState({formData: {...this.state.formData, test_location: v.id}});
              if (this.props.onLocationSelect) {
                this.props.onLocationSelect(v);
              }
            }}
            isSearchable
          />
          {this.props.testResult || (
            <div className="pulseTextBodySmall pulse-color-gray-600">{this.props.helpText}</div>
          )}
          <div className="mt-2">
            <Button
              onClick={this.handleRunTest}
              disabled={this.state.isRunningTest || this.props.disabled}
              data-wizard="devices-testrunner-run-test"
              className="w-100"
            >
              {this.state.isRunningTest && <Icon icon={FiLoader} className="pulseSpin" />}
              Run Test
            </Button>
          </div>
        </div>
      </>
    );
  }
}

TestRunner.defaultProps = {
  locations: [], // list of locations to display in dropdown, **as MonitoringServer dicts**
  disabled: false,
  onStart: null, // handler to be called when test starts
  onSuccess: null, // handler to be called on successful execution
  onError: null, // handler to be called on error
  runTestURL: "", // URL to post to
  data: {}, // service data to be posted (usually - service form data)
  scriptGetter: null, // method to be called to retrieve API/Transaction script
  testResult: null,
  selectedLocation: null, // Added for shared state
  onLocationSelect: null, // Added for shared state
};
